import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { supabase, isSupabaseConfigured } from '../lib/supabase'
import api from '../lib/api'

interface User {
  id: string
  email: string
  businessName?: string
  fullName?: string
  avatarUrl?: string
}

interface AuthContextType {
  user: User | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, businessName?: string, fullName?: string) => Promise<void>
  signOut: () => Promise<void>
  signInWithGoogle: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const syncProfileData = async (_googleAvatarUrl?: string) => {
    try {
      const me = await api.getMe() as { profile?: { language?: string, avatar_url?: string } }
      const language = me?.profile?.language
      if (language === 'en' || language === 'he') {
        localStorage.setItem('quor_locale', language)
        window.dispatchEvent(new Event('quor-locale-sync'))
      }
      // Prefer custom avatar over Google avatar
      const customAvatar = me?.profile?.avatar_url
      if (customAvatar) {
        setUser(prev => prev ? { ...prev, avatarUrl: customAvatar } : null)
      }
    } catch {
      // best-effort only
    }
  }

  useEffect(() => {
    if (!isSupabaseConfigured() || !supabase) {
      // Demo mode - check localStorage
      const savedUser = localStorage.getItem('quor_user')
      if (savedUser) {
        setUser(JSON.parse(savedUser))
      }
      setLoading(false)
      return
    }

    // Rely solely on onAuthStateChange for session management.
    // This correctly handles:
    // - Existing sessions (from localStorage)
    // - OAuth redirects (PKCE code exchange / implicit hash tokens)
    // - Token refreshes
    // The INITIAL_SESSION event fires only after any URL-based auth
    // (Google OAuth code exchange) has completed, avoiding race conditions.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        const metadata = session.user.user_metadata || {}
        setUser({
          id: session.user.id,
          email: session.user.email || '',
          businessName: metadata.business_name,
          fullName: metadata.full_name || metadata.name,
          avatarUrl: metadata.avatar_url || metadata.picture
        })
        api.setToken(session.access_token)
        // Fire-and-forget: don't block auth state on profile sync
        syncProfileData(metadata.avatar_url || metadata.picture)
      } else {
        setUser(null)
        api.setToken(null)
      }
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    if (!isSupabaseConfigured() || !supabase) {
      // Demo mode - create fake user
      const demoUser = { id: 'demo', email, businessName: 'Demo Business' }
      setUser(demoUser)
      localStorage.setItem('quor_user', JSON.stringify(demoUser))
      return
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })

    if (error) throw error

    if (data.session) {
      api.setToken(data.session.access_token)
    }
  }

  const signUp = async (email: string, password: string, businessName?: string, fullName?: string) => {
    if (!isSupabaseConfigured() || !supabase) {
      // Demo mode
      const demoUser = { id: 'demo', email, businessName, fullName }
      setUser(demoUser)
      localStorage.setItem('quor_user', JSON.stringify(demoUser))
      return
    }

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          business_name: businessName,
          full_name: fullName
        }
      }
    })

    if (error) throw error
  }

  const signOut = async () => {
    if (!isSupabaseConfigured() || !supabase) {
      setUser(null)
      localStorage.removeItem('quor_user')
      return
    }

    await supabase.auth.signOut()
    api.setToken(null)
    setUser(null)
  }

  const signInWithGoogle = async () => {
    if (!isSupabaseConfigured() || !supabase) {
      throw new Error('Google sign in requires Supabase configuration')
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin
      }
    })

    if (error) throw error
  }

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      signIn,
      signUp,
      signOut,
      signInWithGoogle
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
